import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Controller, useForm } from "react-hook-form";
import { Link as RouterLink } from "react-router-dom";
import {
  AUTONOMY_POSTURES,
  updateVisitStartElementGovernance,
} from "services/fde/tciGovernanceService";
import {
  filterElementGovernance,
  getVisitStartConfigurableElements,
  getVisitStartCoreElements,
  getVisitStartElements,
  resolveEnabledElements,
} from "services/fde/visitStartElementsCatalog";

const splitLines = (value) =>
  (value || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const joinLines = (items) => (Array.isArray(items) ? items.join("\n") : "");

function buildDefaultFormValues(profile) {
  const enabled = new Set(resolveEnabledElements(profile));
  const governance =
    profile?.elementGovernance &&
    typeof profile.elementGovernance === "object" &&
    !Array.isArray(profile.elementGovernance)
      ? profile.elementGovernance
      : {};
  const values = {
    savoirCareOnVisitStartComplete:
      profile?.savoirCareOnVisitStartComplete === true,
  };
  getVisitStartElements().forEach((element) => {
    const elementId = element.elementId;
    const entry = governance[elementId] || {};
    values[elementId] = {
      autonomyPosture:
        entry.autonomyPosture || element.defaultAutonomyPosture || "balanced",
      mustDoText: joinLines(entry.mustDo),
      mustNotDoText: joinLines(entry.mustNotDo),
      enabled: enabled.has(elementId),
    };
  });
  return values;
}

function ElementGovernanceCard({
  element,
  control,
  enabled,
  canEdit,
  showEnableToggle = false,
}) {
  const disabled = !canEdit || !enabled;
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      data-testid={`visit-start-governance-${element.elementId}`}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: 0.5,
        }}
      >
        <Typography variant="subtitle2">{element.label}</Typography>
        {showEnableToggle ? (
          <Controller
            name={`${element.elementId}.enabled`}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                sx={{ mr: 0 }}
                control={
                  <Switch
                    checked={Boolean(field.value)}
                    onChange={(_, checked) => field.onChange(checked)}
                    disabled={!canEdit}
                    inputProps={{
                      "data-testid": `visit-start-governance-enabled-${element.elementId}`,
                    }}
                  />
                }
                label={field.value ? "Enabled" : "Disabled"}
              />
            )}
          />
        ) : null}
      </Box>
      {!enabled && !showEnableToggle ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          This element is disabled. Enable it on{" "}
          <RouterLink to="/dashboard/tci-manager/tcis/visit-start">
            Manage TCIs
          </RouterLink>{" "}
          before editing per-element governance.
        </Typography>
      ) : null}
      {!enabled && showEnableToggle ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          This element is disabled. Turn it on to edit per-element governance.
        </Typography>
      ) : null}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Controller
          name={`${element.elementId}.autonomyPosture`}
          control={control}
          render={({ field }) => (
            <FormControl fullWidth disabled={disabled}>
              <InputLabel id={`visit-start-posture-${element.elementId}`}>
                Autonomy posture
              </InputLabel>
              <Select
                {...field}
                labelId={`visit-start-posture-${element.elementId}`}
                label="Autonomy posture"
                SelectDisplayProps={{
                  "data-testid": `visit-start-posture-${element.elementId}`,
                }}
              >
                {AUTONOMY_POSTURES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
        <Controller
          name={`${element.elementId}.mustDoText`}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Must do (one per line)"
              multiline
              minRows={3}
              disabled={disabled}
              inputProps={{
                "data-testid": `visit-start-must-do-${element.elementId}`,
              }}
            />
          )}
        />
        <Controller
          name={`${element.elementId}.mustNotDoText`}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Must not do (one per line)"
              multiline
              minRows={3}
              disabled={disabled}
              inputProps={{
                "data-testid": `visit-start-must-not-do-${element.elementId}`,
              }}
            />
          )}
        />
      </Box>
    </Paper>
  );
}

/**
 * Per-element enable / autonomy / mustDo / mustNotDo editor for VisitStart.
 * Batches enabledElements + elementGovernance on one Save. Server merge keeps
 * categories the caller is not allowed to govern.
 */
const VisitStartElementGovernanceSection = ({
  institutionId,
  profile,
  canEdit,
  allowedCategories,
  onSaved,
  onError,
  onSuccess,
}) => {
  const [saving, setSaving] = useState(false);
  const categorySet = useMemo(() => {
    const list = Array.isArray(allowedCategories) ? allowedCategories : [];
    return new Set(list.length ? list : ["administrative", "clinical"]);
  }, [allowedCategories]);
  const coreElements = useMemo(
    () =>
      getVisitStartCoreElements().filter((element) =>
        categorySet.has(element.category),
      ),
    [categorySet],
  );
  const configurableElements = useMemo(
    () =>
      getVisitStartConfigurableElements().filter((element) =>
        categorySet.has(element.category),
      ),
    [categorySet],
  );
  const catalogInScope = useMemo(
    () =>
      getVisitStartElements().filter((element) =>
        categorySet.has(element.category),
      ),
    [categorySet],
  );

  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: buildDefaultFormValues(profile),
  });

  useEffect(() => {
    reset(buildDefaultFormValues(profile));
  }, [profile, reset]);

  const formValues = watch();

  const onSave = async (values) => {
    if (!institutionId || !canEdit) return;
    setSaving(true);
    onError?.(null);
    onSuccess?.(null);
    try {
      const nextEnabled = [];
      catalogInScope.forEach((element) => {
        const elementId = element.elementId;
        if (element.managerConfigurable === false) {
          nextEnabled.push(elementId);
          return;
        }
        if (values[elementId]?.enabled) nextEnabled.push(elementId);
      });
      const nextGovernance = {};
      nextEnabled.forEach((elementId) => {
        const row = values[elementId] || {};
        nextGovernance[elementId] = {
          autonomyPosture: row.autonomyPosture || "balanced",
          mustDo: splitLines(row.mustDoText),
          mustNotDo: splitLines(row.mustNotDoText),
        };
      });
      const elementGovernance = filterElementGovernance(
        nextGovernance,
        nextEnabled,
      );
      const result = await updateVisitStartElementGovernance({
        institutionId,
        enabledElements: nextEnabled,
        elementGovernance,
        savoirCareOnVisitStartComplete:
          values.savoirCareOnVisitStartComplete === true,
      });
      onSuccess?.("VisitStart element governance saved.");
      await onSaved?.(result);
    } catch (err) {
      onError?.(err?.message || "Failed to save VisitStart element governance.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box data-testid="visit-start-element-governance" sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        VisitStart element governance
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enable or disable configurable elements, then set autonomy posture and
        must-do / must-not-do guidance. Patient Identity stays always-on because
        Photo ID capture is injected after it and registration-desk handoff
        needs a remaining administrative step. Clinic Leader (fde-dc) governs
        clinical elements; Clinic Manager (Administrative) governs administrative
        elements. SavoirCare board cards stay off by default. When the toggle is
        on, completing a VisitStart session writes an incoming board card for
        this institution.
      </Typography>

      {!canEdit ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          TCI Manager or institution admin access is required to edit VisitStart
          element governance. Clinic Leader (fde-dc) can edit clinical elements
          only; Clinic Manager (Administrative) can edit administrative elements
          only. Holding both grants shows the union.
        </Alert>
      ) : null}

      <Box component="form" onSubmit={handleSubmit(onSave)} noValidate>
        <Controller
          name="savoirCareOnVisitStartComplete"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              sx={{ mb: 2 }}
              control={
                <Switch
                  checked={Boolean(field.value)}
                  onChange={(_, checked) => field.onChange(checked)}
                  disabled={!canEdit}
                  inputProps={{
                    "data-testid": "visit-start-savoircare-toggle",
                  }}
                />
              }
              label="SavoirCare on VisitStart complete (off by default)"
            />
          )}
        />
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Core
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
          {coreElements.map((element) => (
            <ElementGovernanceCard
              key={element.elementId}
              element={element}
              control={control}
              enabled
              canEdit={canEdit}
            />
          ))}
        </Box>

        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Configurable
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
          {configurableElements.map((element) => (
            <ElementGovernanceCard
              key={element.elementId}
              element={element}
              control={control}
              enabled={Boolean(formValues?.[element.elementId]?.enabled)}
              canEdit={canEdit}
              showEnableToggle
            />
          ))}
        </Box>

        {canEdit ? (
          <LoadingButton
            type="submit"
            variant="contained"
            loading={saving}
            data-testid="visit-start-element-governance-save"
          >
            Save element governance
          </LoadingButton>
        ) : null}
      </Box>

      <Divider sx={{ my: 3 }} />
    </Box>
  );
};

export default VisitStartElementGovernanceSection;
